import {Component, EventEmitter, Inject, isDevMode, OnInit, Output} from '@angular/core';
import {NavigationEnd, Route, Router} from '@angular/router';
import {map} from 'rxjs/internal/operators/map';
import {distinctUntilChanged, filter} from 'rxjs/operators';
import {
    MX_APPLICATION_PROPERTY_SERVICE_TOKEN,
    MxApplicationPropertiesService
} from '../../../../shared/services/mx-application-property.service';
import {MX_ENVIRONMENT_TOKEN, MxEnvironmentService} from '../../../../shared/services/mx-environment.service';
import {MX_PERMISSION_TOKEN, MxPermissionService} from '../../../../shared/services/mx-permission.service';
import {BreadCrumb} from '../../../../shared/services/mx-title-and-breadcrumbs.service';
import {
    MenuItem,
    MX_NAVIGATION_MENU_ELEMENTS_TOKEN,
    MxNavigationMenuElementsService
} from '../../services/mx-navigation-menu-elements.service';
import {MxRecentlyUsedService} from '../../services/mx-recently-used.service';
import {MX_TRANSLATE_SERVICE_TOKEN, MxTranslateService} from "../../../../shared/services/mx-translate.service";


@Component({
    selector: 'mx-navigation-menu',
    standalone: false,
    template: `
        <mx-navigation-menu-items [items]="items" [activeMenuPath]="activeMenuPath" class="main-menu">
        </mx-navigation-menu-items>`,
    styleUrls: ['./mx-navigation-menu.component.scss']
})
export class MxNavigationMenuComponent implements OnInit {
    @Output()
    menuItemSelected = new EventEmitter<void>();

    items: MenuItem[];

    routesMap: Map<string, {activeMenuPath: string, item: MenuItem}> = new Map<string, {activeMenuPath: string, item: MenuItem}>();

    activeUrl: string = '';
    activeMenuPath: string = '';

    constructor(
        private router: Router,
        public recentlyUsedService: MxRecentlyUsedService,
        @Inject(MX_PERMISSION_TOKEN) private permissionService: MxPermissionService,
        @Inject(MX_NAVIGATION_MENU_ELEMENTS_TOKEN) public menuElementsService: MxNavigationMenuElementsService,
        @Inject(MX_APPLICATION_PROPERTY_SERVICE_TOKEN) private applicationProperties: MxApplicationPropertiesService,
        @Inject(MX_ENVIRONMENT_TOKEN) private environmentService: MxEnvironmentService,
        @Inject(MX_TRANSLATE_SERVICE_TOKEN) public translateService: MxTranslateService
    ) {
    }

    ngOnInit(): void {
        this.items = this.initMenuItems(this.menuElementsService.getMenuElements());
        this.putMenuElem(this.items, '');
        this.selectMenu(this.router.url);
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            map((value: NavigationEnd) => value.url),
            distinctUntilChanged()
        )
            .subscribe(value => {
                this.selectMenu(value);
            });
        if (isDevMode()) {
            this.debugNotMenuRoutings();
        }
    }

    private initMenuItems(items: MenuItem[]): MenuItem[] {
        let convertedItems: MenuItem[] = [];
        for (let item of items) {
            if (item.developMode === true && this.environmentService.getEnvironmentVariable('production')) {
                continue;
            }
            let newItem: MenuItem = {...item, toShow: true};
            if (item.label) {
                newItem.label = item.label;
            }

            if (item.link && !item.externalRedirect) {
                let route = this.getRouting(item.link);

                if (route) {
                    if (route.data && route.data.permission) {
                        if (item.permission) {
                            console.debug('Found permission', item.permission, 'for', newItem.label, 'link',
                                newItem.link, 'and for route', route);
                        } else {
                            // console.debug('Using route permission', route.data.permission, 'for', label, 'link', link);
                            item.permission = route.data.permission;
                        }
                    }
                } else {
                    console.debug('Cannot find route for', newItem.label, 'link', newItem.link, ', hidding menu item');
                    newItem.toShow = false;
                }

                if (!item.permission && newItem.toShow) {
                    console.debug('No permission for', newItem.label, 'link', newItem.link);
                }
            }

            if (newItem.toShow && item.permission) {
                newItem.toShow = this.permissionService.hasPermission(item.permission);
            }

            if (newItem.toShow && item.items) {
                newItem.items = this.initMenuItems(item.items);
                if (newItem.items.length === 0) {
                    console.debug('No items for', newItem.label, ', hidding menu item');
                    newItem.toShow = false;
                }
            }

            if (newItem.isRecentlyUsed) {
                newItem.toShow = true;
            }

            if (newItem.toShow && !item.hasAppProperty) {
                convertedItems.push(newItem);
            } else if (item.hasAppProperty) {
                this.applicationProperties.getProperty(item.hasAppProperty)
                    .subscribe(value => {
                        newItem.toShow = value && !!value.trim();
                        if (newItem.toShow) {
                            convertedItems.push(newItem);
                        }
                    });
            }
        }
        return convertedItems;
    }

    private debugNotMenuRoutings(): void {
        for (let route of this.router.config) {
            let link = '/' + route.path;

            if (link === '/' ||
                link.startsWith('/4') ||
                link.startsWith('/5') ||
                link.startsWith('/*') ||
                link.includes(':')) {
                continue;
            }

            if (!this.isInMenu(link, this.menuElementsService.getMenuElements())) {
                console.debug('Cannot found routing link', link, 'in menu');
            }
        }
    }

    private isInMenu(link: string, items: MenuItem[]): boolean {
        for (let item of items) {
            if (item.link === link) {
                return true;
            }
            if (item.items && item.items.length !== 0 && this.isInMenu(link, item.items)) {
                return true;
            }
        }

        return false;
    }


    private getRouting(link: string): Route {
        let routingPath = link.substr(1);

        let foundRoute = null;
        let routes = [...this.router.config];
        for (let route of routes) {
            if (route.path === routingPath) {
                foundRoute = route;
                break;
            }
        }

        return foundRoute;
    }

    putMenuElem(items: Array<MenuItem>, labels: string): void {
        items.forEach(item => {
            if (item.link) {
                this.routesMap.set(item.link, {activeMenuPath: labels + item.link, item});
            } else {
                this.putMenuElem(item.items, labels + '/' + item.label);
            }
        });
    }

    selectMenu(link: string): void {
        let newLink = link;
        let paramsIndex = newLink.indexOf('?');
        if (paramsIndex > -1) {
            newLink = newLink.substring(0, paramsIndex);
        }
        if (this.activeUrl !== newLink) {
            let nextPath = newLink.slice(1).indexOf('/');
            if (nextPath > 0) {
                this.activeUrl = newLink.slice(0, nextPath + 1 );
            } else {
                this.activeUrl = newLink;
            }
            let activePathWithItem = this.routesMap.get(this.activeUrl);
            if (activePathWithItem) {
                this.activeMenuPath = activePathWithItem.activeMenuPath;
            } else {
                this.activeMenuPath = '';
            }

            if (activePathWithItem && activePathWithItem.activeMenuPath.endsWith(link)) {
                this.addToRecentlyUsed(activePathWithItem.item);
            }

            this.menuItemSelected.emit();
        }
    }

    private addToRecentlyUsed(item: MenuItem): void {
        if (item) {
            let breadcrumbsConfig: Array<BreadCrumb> = [
                {id: 'list', link: item.link, icon: item.icon, tooltip: item.label,
                    type: item.link, title: item.label}];
            this.recentlyUsedService.addToRecentlyOpened(breadcrumbsConfig);
        }
    }
}
